import { ShapeStreamOptions } from "@electric-sql/client";
import { DatabaseConnectionManager } from "./database-connection-manager";
import { logger } from "@/modules/logger";
import { clientEnvModule } from "@/modules/client-env";
import { getElectricAuthHeaders } from "@/domains/database/utils/auth";
import { SYNC_TABLES } from "@/domains/database/models";
import { objectModule } from "@/modules/object";

type SyncHandle = { unsubscribe: () => void };

export class ElectricSyncManager {
  private syncHandles = new Map<string, SyncHandle>();
  private isSyncStarted: boolean = false;

  constructor(private connectionManager: DatabaseConnectionManager) {}

  async startSync(): Promise<void> {
    if (!this.connectionManager.isReady()) {
      throw new Error("[ElectricSyncManager] Database not initialized. Call init() first.");
    }

    if (this.isSyncStarted) {
      logger.info({
        message: "[ElectricSyncManager] Sync already started, skipping",
      });

      return;
    }

    logger.info({
      message: "[ElectricSyncManager] Starting smart objects sync",
      info: { baseUrl: clientEnvModule.getElectricBaseUrl() },
    });

    const db = this.connectionManager.getDatabase();
    const electric = db.electric;

    const baseOptions: Pick<ShapeStreamOptions, "url" | "headers" | "backoffOptions"> = {
      url: clientEnvModule.getElectricBaseUrl(),
      headers: {
        Authorization: async (): Promise<string> => {
          const headers = await getElectricAuthHeaders();
          return headers.Authorization;
        },
      },
      backoffOptions: {
        initialDelay: 1_000,
        maxDelay: 60_000,
        multiplier: 3,
      },
    };

    // Start per-table sync handles in parallel; if any fail, roll back started ones
    const enabledTables = SYNC_TABLES.filter(tableConfig => tableConfig.enabled);

    const syncStartPromises = enabledTables.map(tableConfig => {
      return electric
        .syncShapeToTable({
          shape: {
            ...baseOptions,
            parser: tableConfig.parser,
            params: { table: tableConfig.table, columns: tableConfig.columns as string[] },
          },
          shapeKey: tableConfig.shapeKey,
          table: tableConfig.table,
          primaryKey: [...tableConfig.primaryKey],
        })
        .then(handle => ({
          shapeKey: tableConfig.shapeKey,
          handle: handle as unknown as SyncHandle,
        }));
    });

    const syncStartResults = await Promise.allSettled(syncStartPromises);

    const fulfilledResults = syncStartResults.filter(
      (result): result is PromiseFulfilledResult<{ shapeKey: string; handle: SyncHandle }> =>
        result.status === "fulfilled"
    );

    const rejectedResults = syncStartResults.filter(
      (result): result is PromiseRejectedResult => result.status === "rejected"
    );

    if (rejectedResults.length > 0) {
      for (const fulfilled of fulfilledResults) {
        try {
          fulfilled.value.handle.unsubscribe();
        } catch (unknownErr) {
          const error = unknownErr as Error;
          logger.error({
            message: "[ElectricSyncManager] Error unsubscribing after partial start",
            info: { error: error.message },
          });
        }
      }

      const firstError = rejectedResults[0].reason as Error;
      throw firstError;
    }

    for (const fulfilled of fulfilledResults) {
      this.syncHandles.set(fulfilled.value.shapeKey, fulfilled.value.handle);
    }

    this.isSyncStarted = true;
  }

  async stopSync(): Promise<void> {
    if (!this.isSyncStarted) return;

    logger.info({
      message: "[ElectricSyncManager] Stopping sync",
    });

    try {
      for (const [shapeKey, handle] of this.syncHandles.entries()) {
        try {
          handle.unsubscribe();
        } catch (unknownErr) {
          const error = unknownErr as Error;
          logger.error({
            message: "[ElectricSyncManager] Error unsubscribing handle",
            info: { shapeKey, error: objectModule.safeErrorAsJson(error) },
          });
        }
      }
      this.syncHandles.clear();

      this.isSyncStarted = false;

      logger.info({
        message: "[ElectricSyncManager] Sync stopped successfully",
      });
    } catch (unknownErr) {
      const error = unknownErr as Error;
      logger.error({
        message: "[ElectricSyncManager] Error stopping sync",
        info: { error: objectModule.safeErrorAsJson(error) },
      });
      throw error;
    }
  }

  isActive(): boolean {
    return this.isSyncStarted;
  }
}
